import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, X, Share2, Users } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const MEDAL_COLORS = {
  1: { bg: "#FFD700", text: "#0A090D", label: "🥇 1st" },
  2: { bg: "#C0C0C0", text: "#0A090D", label: "🥈 2nd" },
  3: { bg: "#CD7F32", text: "#0A090D", label: "🥉 3rd" },
};

/**
 * Trophy Room — celebratory modal shown after a user finishes a Daily Smash
 * while they are a participant in a group challenge. Fetches the current
 * group leaderboard, highlights the user's placement, and offers a
 * one-click share of their result.
 *
 * Only renders when `code` is truthy AND the group leaderboard returns
 * show_group=true. Silent no-op otherwise.
 */
export const TrophyRoomModal = ({ code, currentUserId, onClose }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [confetti, setConfetti] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    if (!code) return;
    fetch(`${API}/api/daily/challenge/${code}/group`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => {
        if (!d.show_group) { setErr("waiting"); return; }
        setData(d);
        // Only launch confetti if user is in the top 3.
        const me = d.rows.find((r) => r.user_id === currentUserId);
        if (me && me.rank <= 3) {
          const bits = Array.from({ length: 42 }).map((_, i) => ({
            id: i,
            left: Math.random() * 100,
            delay: Math.random() * 0.4,
            duration: 1.6 + Math.random() * 1.4,
            color: ["#FF107A", "#00E5FF", "#FFD700", "#A855F7"][i % 4],
            rot: Math.random() * 360,
          }));
          setConfetti(bits);
        }
      })
      .catch(() => setErr("failed"));
  }, [code, currentUserId]);

  const shareResult = async () => {
    if (!data) return;
    const me = data.rows.find((r) => r.user_id === currentUserId);
    if (!me) return;
    const url = `${window.location.origin}/api/share/daily/${code}`;
    const text = `I ranked #${me.rank} in the Group Smash · ${me.score} pts. Beat me → ${url}`;
    try {
      if (navigator.share) {
        await navigator.share({ text, url });
      } else {
        await navigator.clipboard.writeText(text);
      }
    } catch {}
  };

  if (!code || err === "failed") return null;
  if (err === "waiting") return null;
  if (!data) return null;

  const me = data.rows.find((r) => r.user_id === currentUserId);
  if (!me) return null;
  const medal = MEDAL_COLORS[me.rank];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        data-testid="trophy-room-backdrop"
        className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-md flex items-end sm:items-center justify-center p-3"
        onClick={onClose}
      >
        {/* Confetti overlay */}
        {confetti.length > 0 && (
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            {confetti.map((c) => (
              <motion.div
                key={c.id}
                initial={{ y: -20, rotate: 0, opacity: 1 }}
                animate={{ y: "110vh", rotate: c.rot }}
                transition={{ duration: c.duration, delay: c.delay, ease: "easeIn" }}
                className="absolute w-2 h-3"
                style={{ left: `${c.left}%`, background: c.color, boxShadow: `0 0 6px ${c.color}` }}
              />
            ))}
          </div>
        )}

        <motion.div
          initial={{ y: 40, scale: 0.96, opacity: 0 }}
          animate={{ y: 0, scale: 1, opacity: 1 }}
          exit={{ y: 40, scale: 0.96, opacity: 0 }}
          transition={{ type: "spring", damping: 22, stiffness: 260 }}
          data-testid="trophy-room-modal"
          className="relative w-full max-w-sm rounded-3xl border border-[#FFD700]/50 overflow-hidden"
          style={{
            background: "radial-gradient(circle at 20% 10%, rgba(255,215,0,0.32), transparent 55%)," +
                        "linear-gradient(160deg, #16141D 0%, #0A090D 100%)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            data-testid="trophy-room-close"
            onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 border border-white/15 flex items-center justify-center hover:border-[#FF107A]/60"
          >
            <X size={14} className="text-[#FDFBF7]" />
          </button>

          <div className="p-6">
            <div className="text-[10px] tracking-[0.35em] uppercase font-bold text-[#FFD700] flex items-center gap-2">
              <Trophy size={12} /> Trophy Room
            </div>
            <div className="mt-3 font-display text-4xl leading-[0.9]" data-testid="trophy-room-headline">
              {me.rank === 1 ? "You took the crown." :
                me.rank === 2 ? "Silver medal, gold effort." :
                me.rank === 3 ? "Bronze — top three." :
                `You placed #${me.rank}.`}
            </div>
            {medal && (
              <div
                className="mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1.5 font-bold text-sm"
                style={{ background: medal.bg, color: medal.text }}
                data-testid="trophy-room-medal"
              >
                {medal.label}
              </div>
            )}
            <div className="mt-4 text-sm text-[#FDFBF7]/70 flex items-center gap-2">
              <Users size={14} /> {data.played_count} playing this group
            </div>

            {/* Top 3 podium mini-view */}
            <div className="mt-5 space-y-1.5" data-testid="trophy-room-podium">
              {data.rows.slice(0, Math.max(3, me.rank)).map((r) => {
                const isMe = r.user_id === currentUserId;
                return (
                  <div
                    key={r.user_id || r.rank}
                    data-testid={`trophy-row-${r.rank}`}
                    className={`flex items-center gap-3 rounded-xl border px-3 py-2 ${
                      isMe ? "border-[#FFD700]/70 bg-[#FFD700]/10"
                            : "border-white/10 bg-black/40"
                    }`}
                  >
                    <div className={`w-7 text-center font-display text-lg tabular ${
                      r.rank === 1 ? "text-[#FFD700]"
                      : r.rank === 2 ? "text-[#00E5FF]"
                      : r.rank === 3 ? "text-[#FF6B35]" : "text-[#FDFBF7]/60"
                    }`}>{r.rank}</div>
                    {r.picture ? (
                      <img src={r.picture} alt="" className="w-8 h-8 rounded-full border border-white/20" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-[#FF107A]/20 border border-[#FF107A]/40 flex items-center justify-center text-xs font-bold">
                        {r.name?.[0]?.toUpperCase() || "?"}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#FDFBF7] truncate">
                        {r.name}
                        {isMe && (
                          <span className="text-[9px] tracking-[0.2em] uppercase text-[#FFD700] ml-1">You</span>
                        )}
                      </div>
                    </div>
                    <div className="font-display text-lg tabular text-[#FFD700]">{r.score}</div>
                  </div>
                );
              })}
            </div>

            <button
              data-testid="trophy-room-share"
              onClick={shareResult}
              className="mt-5 w-full rounded-full bg-[#FF107A] text-[#FDFBF7] py-3 font-bold glow-pink flex items-center justify-center gap-2"
            >
              <Share2 size={16} /> Share your placement
            </button>
            <button
              data-testid="trophy-room-view-group"
              onClick={() => nav(`/daily/vs/${code}`)}
              className="mt-2 w-full rounded-full border border-[#FFD700]/50 bg-[#FFD700]/10 text-[#FFD700] py-3 font-bold flex items-center justify-center gap-2"
            >
              <Trophy size={16} /> View full group leaderboard
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
